import React, { useRef, useState } from 'react';
import { ChildrenType } from 'src/types/generic-types';
import { createCtx } from 'src/utility/Helper';

export const SOURCE_ACTION = {
  BACK_TO_HOME: 'BACK_TO_HOME',
  NAVIGATION_ITEM: 'NAVIGATION_ITEM',
} as const;

type SourceActionType = keyof typeof SOURCE_ACTION;
type StateType = { isHidden: boolean; isUpdated: boolean; params: Record<string, unknown> };
type EffectFunction = (state: StateType, action?: Action, sourceAction?: SourceActionType) => void;
type Action = 'CANCEL' | 'OK';

type DialogContextType = {
  state: StateType;
  setUpdate: (value: boolean) => void;
  hideDialog: (action: Action) => void;
  showDialog: (label?: SourceActionType, params?: Record<string, unknown>) => void;
  watchEffect: (callbackFn: EffectFunction) => void;
};

const [useCtx, DialogCtxtProvider] = createCtx<DialogContextType>('DialogContextProvider');

// source label is used to find out where it is showed, to know what behavior to do.
let sourceLabel: SourceActionType | undefined;
let sourceParams: Record<string, unknown> = {};
export const useDialogContext = (): DialogContextType => {
  const { state, showDialog: onShow, hideDialog: onHide, setUpdate } = useCtx();
  const observables = useRef<Map<string, EffectFunction>>(new Map()).current;

  const hideDialog = (action: Action) => {
    Array.from(observables.values()).forEach((fn) =>
      fn({ ...state, params: sourceParams, isHidden: true }, action, sourceLabel)
    );
    // reset show label
    sourceLabel = undefined;
    sourceParams = {};
    if (action === 'OK') {
      setUpdate(false);
    }
    onHide(action);
  };

  const showDialog = (label?: SourceActionType, params?: Record<string, unknown>) => {
    sourceLabel = label;
    sourceParams = params || {};
    Array.from(observables.values()).forEach((fn) => fn({ ...state, params: sourceParams, isHidden: false }));
    onShow();
  };

  const watchEffect = (callbackFn: EffectFunction) => {
    const alreadyExists = observables.has(callbackFn.toString());

    if (alreadyExists) return;

    observables.set(callbackFn.toString(), callbackFn);
  };

  return { state, showDialog, hideDialog, watchEffect, setUpdate };
};

export const DialogContextProvider = ({ children }: { children: ChildrenType }): React.JSX.Element => {
  const [state, setState] = useState(() => ({ isHidden: true, isUpdated: false, params: {} }));

  const hideDialog = () => {
    setState((prev) => ({ ...prev, isHidden: true }));
  };

  const showDialog = () => {
    setState((prev) => ({ ...prev, isHidden: false }));
  };
  /**
   * DISPATCHER used to detects when the user has filled some fields in the form
   * @param value
   */
  const setUpdate = (value: boolean) => {
    setState((prev) => ({ ...prev, isUpdated: value }));
  };

  return (
    <DialogCtxtProvider value={{ state, setUpdate, hideDialog, showDialog, watchEffect: () => undefined }}>
      {children}
    </DialogCtxtProvider>
  );
};
